import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { DownloadIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useUser } from '../context/user-context'
import { extractUserDetails } from '../context/user-util'
import UserDetailsForm from '../components/settings/user-details-form'

export const AccountSettings = () => {
  const { user } = useUser()
  const [userDetails, setUserDetails] = useState(null)

  useEffect(() => {
    if (user) {
      setUserDetails(extractUserDetails(user))
    }
  }, [user])

  return (
    <Card className="bg-transparent h-full border-none w-[40%] m-auto">
      <CardHeader className="bg-none bg-transparent">
        <CardTitle className="text-lg font-medium text-gray-400 tracking-wider mb-4">
          Update your account information here
        </CardTitle>
      </CardHeader>

      {userDetails ? (
        <UserDetailsForm user={userDetails} />
      ) : (
        <div className="h-full w-full">
          <Skeleton className="bg-gray-700 w-full h-10 my-8" />
          <Skeleton className="bg-gray-700 w-full h-10 my-8" />
          <Skeleton className="bg-gray-700 w-full h-10 my-8" />
          <Skeleton className="bg-gray-700 h-20 w-20 rounded-full my-8 " />
          <Skeleton className="bg-gray-700 w-full h-10 my-8" />
        </div>
      )}
    </Card>
  )
}

export const Plan = ({ title, price, maxUsers, description, features, selected, onSelect, actionButton }) => {
  return (
    <Card
      onClick={onSelect}
      className={`p-6 rounded-md bg-navbarBackground shadow-lg w-80 cursor-pointer ${
        selected ? "border-2 border-orange-500" : "border border-transparent"
      }`}
    >
      <CardHeader className="flex flex-col bg-transparent justify-start items-start">
        <div className="inline-flex justify-between w-full">
          <h2 className="font-bold text-[20px] text-primary-foreground tracking-widest">
            {title}
          </h2>
          {selected && (
            <div className="border-1 bg-gray-600 rounded-sm pl-1 pr-1 text-center tracking-wide">
              <span className="align-middle text-sm font-semibold">
                Your choice
              </span>
            </div>
          )}
        </div>
        <p className="text-gray-300 text-[16px] font-semibold">
          {price}{" "}
          <span className="text-gray-500 font-normal">
            (max. {maxUsers} users)
          </span>
        </p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 h-full justify-between mt-4">
        <div className="text-gray-400 text-sm tracking-widest mb-4 leading-tight min-h-[40px] pt-4">
          {description}
        </div>
        <div className={`h-[60px] flex justify-end items-end `}>
          {actionButton}
        </div>
        <hr className="border-gray-700" />
        <div className="min-h-[250px]">
          <p className="font-semibold text-[20px] text-primary-foreground">
            {features.title}
          </p>
          <ul className="list-disc pl-5 mt-2 text-gray-400 text-sm space-y-1 leading-tight">
            {features.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
export const PlanSettings = () => {
  const [selectedPlan, setSelectedPlan] = useState("Free");

  const plans = [
    {
      title: "Free",
      price: "0 €",
      maxUsers: 1,
      description: "For small teams, individual developers and startups.",
      features: {
        title: "Access to:",
        features: [
          "Visualisation of up to 10 components",
          "Basic services interaction mapping",
          "Community support",
          "Open-source agents for Java/Spring Boot and React",
        ],
      },
    },
    {
      title: "Pro",
      price: "TBD €",
      maxUsers: 10,
      description: "For Mid-size companies, growing startups.",
      actionButton: <Button>Buy now</Button>,
      features: {
        title: "Everything in Free, plus:",
        features: [
          "Visualisation of up to 50 components",
          "Advanced services interaction mapping",
          "Historical view (up to 3 months)",
          "Integration with popular CI/CD tools",
          "Email support",
        ],
      },
    },
    {
      title: "Enterprise",
      price: `TBD €`,
      maxUsers: "custom",
      description: "For Large corporations, enterprises.",
      actionButton: (
        //on hover underline
        <Button
          variant="secondary"
          className="border-none text-primary hover:underline font-semibold"
        >
          Contact Sales
        </Button>
      ),
      features: {
        title: "Everything in Pro, plus:",
        features: [
          "Unlimited components visualisation",
          "On-premises deployment option",
          "Extended historical view (up to 2 years)",
          "Advanced security features (role-based access control, audit logs)",
          "Custom integrations (beta)",
          "Dedicated account manager",
          "24/7 priority support",
        ],
      },
    },
  ];

  return (
    <div className="w-full h-screen flex justify-center">
      <div className="flex space-x-4 p-8 h-[700px]">
        {plans.map((plan) => (
          <Plan
            key={plan.title}
            {...plan}
            selected={selectedPlan === plan.title}
            onSelect={() => setSelectedPlan(plan.title)}
          />
        ))}
      </div>
    </div>
  );
};

const CreditCardComponent = () => {
  const [cardNumber, setCardNumber] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");

  return (
    <div className="flex flex-col items-center p-8 max-w-md mx-auto w-[60%]">
      <div className="bg-navbarBackground text-primary-foreground w-full p-5 rounded-xl mb-4 relative bg-[url(/effect.png)]">
        <div className="flex justify-between">
          <p className="text-md font-normal tracking-widest">Credit</p>
          {/* visa logo from cdn */}
          <div>
            <img src="/visa.svg" alt="visa" className="w-14 h-10 mt-[-6px]" />
          </div>
        </div>

        <div className="flex justify-between mt-8 font-mono font-light">
          <div>
            <p className="text-sm">{cardHolder || "Optimus Prime"}</p>
          </div>
        </div>
        <div className="my-2 flex justify-between">
          <p className="text-sm font-mono font-semibold tracking-widest">
            {cardNumber || "**** **** **** 5168"}
          </p>
          <div>
            <p className="text-sm ml-10">{expiry || "09/25"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ListItem = ({ subject, date, value }) => {
  return (
    <div className="grid grid-cols-4 items-center mb-4">
      <p>{subject}</p>
      <p className="m-auto text-gray-500">{date}</p>
      <p className="m-auto text-orange-500">{value}</p>
      <Button variant="secondary" size="icon" className="m-auto">
        <DownloadIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};
export const Billing = () => {
  const items = [
    {
      subject: "vizOps Pro Tier",
      date: "December 2024",
      value: "€ 149.50",
    },
    {
      subject: "vizOps Pro Tier",
      date: "November 2024",
      value: "€ 149.50",
    },
    {
      subject: "vizOps Pro Tier",
      date: "October 2024",
      value: "€ 149.50",
    },
    {
      subject: "vizOps Pro Tier",
      date: "September 2024",
      value: "€ 0.00",
    },
  ];
  return (
    <div className="w-[40%] m-auto">
      <div>
        <h1 className="text-gray-500 tracking-wider font-normal text-xl p-4">
          Card Details
        </h1>
        <Separator />
      </div>
      <div>
        <CreditCardComponent />
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            className="border-0 bg-transparent col-span-1 font-bold"
          >
            Delete
          </Button>
          <Button
            as="label"
            htmlFor="avatar"
            variant="secondary"
            size="sm"
            className="border-0  bg-transparent col-span-1  font-bold text-primary"
          >
            Update
          </Button>
        </div>
        <div>
          <div>
            <h1 className="text-gray-500 tracking-wider font-normal text-xl p-4">
              Payment History
            </h1>
            <Separator />
          </div>
          <div className="mt-4">
            {items.map((item, index) => (
              <ListItem key={index} {...item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default function SettingsPage() {
  return (
    <div className="flex items-center w-full justify-center relative">
      <div className="w-full">
        <AccountSettings />
      </div>
    </div>
  );
}
